import { CommonLayout } from '../commonComponents';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useRouteMatch, Router } from 'react-router';
import { getApplicationDetails } from '../../redux-store/actions/application';
import { ApplicationProps } from '../../redux-store/types/api-types';
import { Location } from 'history';
import { RootStateType } from '../../redux-store/reducers';
import { RiskProfileEnabled, USER_ROLES } from '../../utils/constant';
import { InvestorDetailsLayout } from '../investors/components';
import ContactDetails from './ContactDetails';
import BankDetails from '../investors/bankDetails';
import InvestmentPaymentDetails from '../investors/investmentPaymentDetails';
import DocumentDetails from './DocumentDetails';
import ContributorDetails from './ContributorDetails';
import AuthorisedSignatories from './Authorised';
import Ubo from './ubo';
import { Fatca } from './fatca';
import { Footer } from '../commonComponents';
import { AppBar, Divider, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import DistributorDetails from '../investors/distributorDetails';
import { ExitToApp } from '@mui/icons-material';
import { logout } from '../../redux-store/actions/auth';
import riskProfile from '../investors/riskProfileDetails';
import { getSpecificTopups } from '../../redux-store/actions/onBoarding';
import { LogoEditApplication } from '../Logo';
import TopUpInvestmentPaymentDetails from '../investors/topUpInvestmentPaymentDetails';

export const Routes = ({
  id,
  applicant1ReferenceId,
  topUpApplication,
}: {
  id: string;
  applicant1ReferenceId?: string;
  applicationType?: string;
  topUpApplication?: boolean | null;
}): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { path } = useRouteMatch();

  const {
    auth: { role },
    application: { application: _application },
    investor: { token = '' },
  } = useSelector((store: RootStateType) => store);

  const { applicationNumber } = _application || {};

  useEffect(() => {
    async function fetchApplication() {
      try {
        if (role === USER_ROLES.INVESTOR && !token) {
          history.push(
            `/${topUpApplication ? 'topup' : 'investment-details'}/${applicant1ReferenceId}/details`
          );
          return;
        }
        if (id) {
          topUpApplication
            ? ((await dispatch(getSpecificTopups(Number(id)))) as unknown as ApplicationProps)
            : ((await dispatch(getApplicationDetails(id))) as unknown as ApplicationProps);
        } else {
          history.push('/');
          console.error('No application id found');
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    }
    fetchApplication();
  }, []);

  return (
    <InvestorDetailsLayout
      applicationId={id}
      applicant1ReferenceId={applicant1ReferenceId}
      applicationNumber={applicationNumber || ''}
      isTopUp={!!topUpApplication}
      topUpApplicationNumber={_application?.topupApplicationNumber || ''}>
      <Router history={history}>
        <Switch>
          <Route
            exact
            path={path}
            render={() => (
              <Redirect
                to={{
                  pathname: `${path}/distributor-details`,
                  state: { id, applicant1ReferenceId, topUpApplication },
                }}
                from={location.pathname}
              />
            )}
          />
          <Route path={`${path}/distributor-details`} component={DistributorDetails} />
          <Route path={`${path}/contributor-details`} component={ContributorDetails} />
          {RiskProfileEnabled && <Route path={`${path}/risk-profiles`} component={riskProfile} />}
          <Route path={`${path}/contact-details`} component={ContactDetails} />
          <Route
            path={`${path}/investment-payment-details`}
            component={topUpApplication ? TopUpInvestmentPaymentDetails : InvestmentPaymentDetails}
          />
          <Route path={`${path}/bank-details`} component={BankDetails} />
          <Route path={`${path}/document-details`} component={DocumentDetails} />
          <Route path={`${path}/authorised-signatory`} component={AuthorisedSignatories} />
          <Route path={`${path}/declaration-ubo`} component={Ubo} />
          <Route path={`${path}/fatca`} component={Fatca} />
        </Switch>
      </Router>
    </InvestorDetailsLayout>
  );
};

//For Amc Approver
export const NonIndividualApplicationEdit = ({
  location,
}: {
  location: Location<{ id: string; applicant1ReferenceId?: string; topUpApplication?: boolean }>;
}): JSX.Element => {
  const { id, applicant1ReferenceId, topUpApplication } = location.state || {};
  const dispatch = useDispatch();
  const history = useHistory();
  return (
    <Box
      sx={{
        bgcolor: { xs: '', sm: 'rgba(238, 244, 251, 0.5)' },
      }}>
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'common.white' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: '48px',
            paddingLeft: '48px',
          }}>
          <LogoEditApplication />
          <Box sx={{ display: 'flex' }}>
            <IconButton
              onClick={() => {
                dispatch(logout());
                history.push('/login');
              }}>
              <ExitToApp fontSize="large" sx={{ color: 'primary.main' }} />
            </IconButton>
          </Box>
        </Box>
      </AppBar>
      <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
      <Box sx={{ py: 3, px: { xs: 2, sm: 10 }, mt: 8 }}>
        <Routes
          id={id}
          applicant1ReferenceId={applicant1ReferenceId}
          topUpApplication={topUpApplication}
        />
      </Box>
      <Footer />
    </Box>
  );
};

const NonIndividualApplicationRoutes = ({
  location,
}: {
  location: Location<{
    id: string;
    applicant1ReferenceId?: string;
    applicationType?: string;
    topUpApplication?: boolean | null;
  }>;
}): JSX.Element => {
  const {
    id = '',
    applicant1ReferenceId = '',
    applicationType = '',
    topUpApplication = false,
  } = location.state || {};
  return (
    <CommonLayout>
      <Routes
        id={id}
        applicant1ReferenceId={applicant1ReferenceId}
        applicationType={applicationType}
        topUpApplication={topUpApplication}
      />
    </CommonLayout>
  );
};

export default NonIndividualApplicationRoutes;
